import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { vehicleLandingFacetField } from 'Addons/fitmentSearch/vehicleLandingFacetField.ts';
import VehicleWidget from 'Components/fitmentSearch/vehicleWidget/index.ts';
import { toggle } from 'Core/actions/request.js';
import useScroll from 'Core/hooks/horizontalScroll.ts';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index.js';
import { createFacetSelector } from 'Core/selectors/search.js';
import { prepareResponseFacets } from 'Core/reducers/search/response.js';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import requestConfig from 'Models/uiConfig/requestConfig.js';
import { getRequestFromUri, getUriFromRequest, getVehicleFromUri } from 'Modules/converter/index.js';
import { facets } from 'Modules/serverApi/index.js';
import { DisplayMode } from 'Modules/serverApi/types.ts';
import { cloneSafe } from 'Utils/components.ts';
import Tile from './tile.tsx';
import Subtile from './subtile.ts';
import FilterInput from 'Components/smallComponents/filterInput';

import type { FunctionComponent } from 'react';
import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types.ts';
import type { Params as FilterInputParams } from 'Components/smallComponents/filterInput';
import type { UseScroll } from 'Core/hooks/horizontalScroll.ts';
import type { Facet, FacetRequest, FacetValueFull } from 'Models/index.ts';
import type { ServerModel } from 'Modules/serverApi/types.ts';
import type { Params as TileParams, SubvalueParams } from './tile.tsx';

const vehicleWidgetName = 'FacetTilesVehicleWidget';

interface LetterTileParams extends LetterParams {
  tiles: RepeaterFunctionInvoker<TileParams>;
}
interface FilterLetterParams extends LetterParams {
  currentLetter: string;
  isDisabled: boolean;
  setCurrentLetter: (letter: string) => void;
}
interface LetterParams {
  letter: string;
}

type Params = {
  alphabeticalGroups: RepeaterFunctionInvoker<LetterParams>;
  alphabeticalLetters: RepeaterFunctionInvoker<LetterParams>;
  currentLetter: string;
  hierarchicalGroups: RepeaterFunctionInvoker<TileParams>;
  vehicleWidget: TemplateFunctionInvoker<unknown>;
  isMultiLevel?: boolean;
  view?: string;
  filterInput: TemplateFunctionInvoker<FilterInputParams>;
} & UseScroll;

type Props = {
  template: TemplateFunction<Params>;
  columnBreakpoint?: number;
  excludeFieldPreselection?: string;
  facetField?: string;
  initCollapsed?: boolean;
  isAlwaysActive?: boolean;
  isMultiLevel?: boolean;
  isVehicleWidgetDisabled?: boolean;
  useFilterMode?: boolean;
  view?: string;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
};

const FacetTiles: FunctionComponent<Props> = ({
  template,
  columnBreakpoint,
  excludeFieldPreselection,
  facetField = fitmentSearchConfig.currentCategorySelectionPageField as string,
  initCollapsed,
  isAlwaysActive,
  isMultiLevel,
  isVehicleWidgetDisabled,
  useFilterMode = false,
  view,
  useNativeDropdown,
  useSearchableDropdown,
}) => {
  const dispatch = useDispatch();
  const rootRef = useRef<HTMLElement>(null);

  const [filterValue, setFilterValue] = useState('');

  const [tilesFacet, setTilesFacet] = useState<Facet>();
  const [vehicleUriSelection, setVehicleUriSelection] = useState<FacetRequest[]>([]);

  const { scrollLeft, scrollRight, isLeftBorder, isRightBorder } = useScroll(rootRef);

  const selectedVehicleSelection = useSelector(createSelectedVehicleSelectionSelector);

  const filteredTiles = tilesFacet?.getFilteredValues(filterValue);

  const [currentLetter, setCurrentLetter] = useState<string>('All');
  const alphabeticalGroups = useGroupAlphabetically(filteredTiles);

  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const hierarchicalGroups = useGroupHierarchically(filteredTiles);

  const responseFacet = useSelector(createFacetSelector(facetField)) as Facet;

  useEffect(() => {
    if (!currentTab && hierarchicalGroups.length) {
      setCurrentTab(hierarchicalGroups[0][0].value);
    }
  }, [currentTab, hierarchicalGroups]);

  useEffect(() => {
    if (useFilterMode) {
      setTilesFacet(responseFacet);
    }
  }, [responseFacet, useFilterMode]);

  useEffect(() => {
    if (useFilterMode) {
      return;
    }

    const { isFitmentSearchWidgetsVisible, hideVehicleFromUrl, baseFields } = fitmentSearchConfig;
    const vehicleSelectionFromUri = getVehicleFromUri().selection as FacetRequest[];
    const vehicleFieldSelectionFromUri = getRequestFromUri(window.location).selection.find(
      (v: FacetRequest) => v.field === vehicleLandingFacetField,
    ) as FacetRequest;

    const isPartialVehicleInUrl =
      vehicleSelectionFromUri.length > 0 && vehicleSelectionFromUri.length < baseFields.length;

    const isVehicleWidgetAvailable = isFitmentSearchWidgetsVisible || !isVehicleWidgetDisabled;
    const isVehicleSelected = !isPartialVehicleInUrl || selectedVehicleSelection.length >= baseFields.length;

    const vehicleSelection = [
      ...(isVehicleWidgetAvailable && isVehicleSelected
        ? selectedVehicleSelection
        : vehicleSelectionFromUri.length
          ? vehicleSelectionFromUri
          : []),
    ] as FacetRequest[];

    if (vehicleFieldSelectionFromUri) {
      vehicleSelection.push(vehicleFieldSelectionFromUri);
    }

    const preselection = [
      ...vehicleSelection,
      ...requestConfig.localPreselection,
      ...requestConfig.defaultSelection,
    ]
      .filter((v) => v.field !== excludeFieldPreselection)
      .map((v: FacetRequest) => {
        return { ...v, hidden: v.field !== facetField };
      });

    const facetSelection = { field: facetField, expand: isMultiLevel };

    const extra = excludeFieldPreselection ? { excludeFieldPreselection } : {};
    const request = { selection: [...preselection, facetSelection], extra };

    facets(request).then((response: ServerModel.FacetsResponse) => {
      const precessedResponse = prepareResponseFacets(
        requestConfig.facetResponseHandler(response).Facets,
        [],
      );

      const tilesFacet = precessedResponse?.get(facetField);

      if (tilesFacet?.values?.length) {
        setTilesFacet(tilesFacet);
      } else if (!requestConfig.hasSearchResults) {
        window.location.assign(getUriFromRequest({ selection: vehicleSelection }, { goToSearchPage: true }));
      }
    });

    if (!hideVehicleFromUrl || vehicleSelectionFromUri.length) {
      selectedVehicleSelection.length === 0 && isPartialVehicleInUrl
        ? setVehicleUriSelection(vehicleSelectionFromUri)
        : setVehicleUriSelection(selectedVehicleSelection);
    }
  }, [
    dispatch,
    excludeFieldPreselection,
    facetField,
    isMultiLevel,
    isVehicleWidgetDisabled,
    useFilterMode,
    selectedVehicleSelection,
  ]);

  const vehicleWidget: TemplateFunctionInvoker<unknown> = (templ) => {
    const props = {
      isAlwaysActive: isAlwaysActive ?? false,
      initCollapsed,
      isHidden: isVehicleWidgetDisabled,
      columnBreakpoint,
      doNotRedirectOnVehicleSelect: true,
      name: vehicleWidgetName,
      useNativeDropdown,
      useSearchableDropdown,
      template: templ,
    };
    return (<VehicleWidget {...props} key="VehicleWidget" />) as TemplateResult;
  };

  const toggleFacet = (term: string) => {
    dispatch(
      toggle(
        { field: facetField, term },
        { isSingleValue: responseFacet.displayMode === DisplayMode.Radio, mayDiscardValue: true },
      ),
    );
  };

  const alphabeticalLetters = alphabeticalGroups.map(
    ([letter, values]) =>
      (templ) =>
        cloneSafe(
          templ.call({
            currentLetter,
            isDisabled: letter !== 'All' && !values.length,
            letter,
            setCurrentLetter,
          }),
          null,
          { key: `filterLetter-${letter}` },
        ),
  ) as RepeaterFunctionInvoker<FilterLetterParams>;

  const filteredAlphabeticalGroup = useFilterGroup(currentLetter, alphabeticalGroups, ([letter]) => letter);
  const filteredHierarchicalGroups = useFilterGroup(
    currentLetter,
    hierarchicalGroups,
    ([facetValue]) => facetValue.term,
  );

  const alphabeticalGroupsRepeater = filteredAlphabeticalGroup.map(([letter, values]) => (templ) => {
    const tilesRepeater = values.map((value) => (templ) => {
      const props = {
        template: templ,
        subvalue: value,
        toggleFacet,
        useFilterMode,
        vehicleFromUriSelection: vehicleUriSelection,
      };
      return <Subtile {...props} key={value.term} />;
    }) as RepeaterFunctionInvoker<SubvalueParams>;

    const component = templ.call({ letter, tiles: tilesRepeater });
    return cloneSafe(component, null, { key: letter });
  }) as RepeaterFunctionInvoker<LetterTileParams>;

  const hierarchicalGroupsRepeater = filteredHierarchicalGroups.map(([value, subvalues]) => (templ) => {
    const props = {
      template: templ,
      value,
      subvalues,
      currentTab,
      isMultiLevel,
      toggleFacet,
      useFilterMode,
      vehicleFromUriSelection: vehicleUriSelection,
      setCurrentTab,
      vehicleWidget,
    };
    return <Tile {...props} key={value.term} />;
  }) as RepeaterFunctionInvoker<TileParams>;

  const filterInput = (templ: TemplateFunction<FilterInputParams>) => {
    if (!tilesFacet) {
      return null;
    }

    const props = {
      template: templ,
      filterValue,
      facetName: tilesFacet.name,
      facetField: tilesFacet.field,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value),
    };
    return (<FilterInput {...props} key={tilesFacet.field} />) as TemplateResult;
  };

  const component = template.call({
    alphabeticalGroups: alphabeticalGroupsRepeater,
    alphabeticalLetters,
    currentLetter,
    hierarchicalGroups: hierarchicalGroupsRepeater,
    isMultiLevel,
    view,
    vehicleWidget,
    scrollLeft,
    scrollRight,
    isLeftBorder,
    isRightBorder,
    filterInput,
  });

  return cloneSafe(component, rootRef);
};

const charArray = ['All', '#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

function getFilterChar(str: string) {
  const firstChar = str.charAt(0).toUpperCase();
  return charArray.includes(firstChar) ? firstChar : '#';
}

function useFilterGroup(currentLetter: string, group, keySelector: (value: unknown) => string) {
  return currentLetter !== 'All'
    ? group.filter((value) => getFilterChar(keySelector(value)) === currentLetter)
    : group;
}

type AlphabeticalFacet = [string, FacetValueFull[]][];
function useGroupAlphabetically(values: FacetValueFull[] | undefined): AlphabeticalFacet {
  if (!values?.length) {
    return [];
  }

  return values.reduce(
    (groups, facetValue) => {
      const keyChar = getFilterChar(facetValue.term);
      const groupIndex = groups.map((group) => group[0]).indexOf(keyChar);

      if (groupIndex > 0) {
        (groups[groupIndex][1] as FacetValueFull[]).push(facetValue);
      }

      return [...groups];
    },
    charArray.map((char) => [char, []]),
  ) as AlphabeticalFacet;
}

type FacetSubvaluesPairs = [FacetValueFull, FacetValueFull[]][];
function useGroupHierarchically(values: FacetValueFull[] | undefined): FacetSubvaluesPairs {
  if (!values?.length) {
    return [];
  }

  return values.reduce((pairs, facetValue) => {
    switch (facetValue.treeLevel) {
      // not Tree facet or first level value
      case null:
      case 0:
        pairs.push([facetValue, []]);
        break;
      // subvalue
      default: {
        const value = pairs[pairs.length - 1];

        // there are subvalues but no parent value
        if (value && !value[0].term.includes('>')) {
          // so far only the second level is supported
          if (facetValue.treeLevel === 1) {
            pairs[pairs.length - 1][1].push(facetValue);
          }
        } else {
          pairs.push([facetValue, []]);
        }
        break;
      }
    }
    return pairs;
  }, [] as FacetSubvaluesPairs);
}

export default FacetTiles;
